import React, { useMemo } from "react";
import { useAppContext } from "../../contexts/AppContext";
import { useSelection } from "../../hooks/useSelection";
import { EXIF_FIELDS } from "../../config/fieldDefinitions";
import { Card, CardContent, Separator } from "@exiftrator/shared/components/ui";

function AssistantDiffTab() {
  const { files } = useAppContext();
  const { primaryIndex, selectedIndices } = useSelection();

  const primaryFile = files[primaryIndex];

  const changes = useMemo(() => {
    if (!primaryFile) return [];

    const result = [];
    const original = primaryFile.exifData || {};
    const modified = primaryFile.modifiedExifData || {};

    Object.values(EXIF_FIELDS).forEach((field) => {
      const before = original[field.id] ?? field.defaultValue;
      const after = modified[field.id] ?? field.defaultValue;

      if (before !== after) {
        result.push({
          fieldId: field.id,
          label: field.label,
          before,
          after,
        });
      }
    });

    return result;
  }, [primaryFile]);

  if (!primaryFile) {
    return (
      <Card className="gap-0 border-border-subtle bg-bg-secondary/70 py-0">
        <CardContent className="p-4 text-sm text-text-secondary">
          Select a file to inspect metadata changes.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card className="gap-0 border-border-subtle bg-bg-secondary/70 py-0">
        <CardContent className="p-3 text-xs text-text-secondary">
          {selectedIndices.size} selected • {changes.length} changed field{changes.length === 1 ? "" : "s"} on primary file
        </CardContent>
      </Card>

      {changes.length === 0 ? (
        <Card className="gap-0 border-border-subtle bg-bg-secondary/70 py-0">
          <CardContent className="p-4 text-sm text-text-secondary">
            No differences between original and edited metadata.
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-0 overflow-hidden border-border-subtle bg-bg-secondary/85 py-0">
          <CardContent className="p-0">
            {changes.map((change, index) => (
              <div key={change.fieldId}>
                <div className="grid grid-cols-1 gap-2 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                    {change.label}
                  </p>
                  <p className="text-xs text-text-secondary">Before: {String(change.before || "-")}</p>
                  <p className="text-xs text-text-primary">After: {String(change.after || "-")}</p>
                </div>
                {index < changes.length - 1 && <Separator />}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AssistantDiffTab;
